import moment from 'moment'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { uniqueArr } from 'utils/misc'
import { DATE_FORMAT_MAP, ID_PATTERN_TOKENS, REGEX_PARTS, PATTERN_KEYWORDS } from 'utils/constants'
import STRINGS from 'utils/strings'

// Helper for alphanumeric patterns that require at least one digit
const withDigitRequirement = (charClass, len) => {
  return len ? `(?=[${charClass}]*\\d)[${charClass}]{${len}}` : `(?=[${charClass}]*\\d)[${charClass}]+`
}

const REGEX_EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const passCustomValidation = ({ val = '', customValidation }) => {
  const lastSlash = customValidation.lastIndexOf('/')
  const regex = new RegExp(customValidation.slice(1, lastSlash), customValidation.slice(lastSlash + 1))
  return val.match(regex)
}

/** Parse custom_validations string in /pattern/flags format (e.g. "/^\\d{9}$/g") into a RegExp. */
function parseRegexFromCustomValidation(customValidation) {
  if (typeof customValidation !== 'string' || customValidation.length < 2 || customValidation[0] !== '/') {
    return null
  }
  const lastSlash = customValidation.lastIndexOf('/')
  if (lastSlash <= 0) return null
  const pattern = customValidation.slice(1, lastSlash)
  const flags = customValidation.slice(lastSlash + 1)
  try {
    return new RegExp(pattern, flags)
  } catch {
    return null
  }
}

function getNameError({ val, customValidation }) {
  if (customValidation && !passCustomValidation({ val, customValidation })) {
    return STRINGS.participantValidationValidName
  }
  if (!val || val.length === 0) {
    return STRINGS.participantValidationName
  }
  return null
}

export function getEmailError({ val, customValidation }) {
  if (customValidation && !passCustomValidation({ val, customValidation })) {
    return STRINGS.participantValidationEmailInvalid
  }
  if (!val || val.length === 0) {
    return STRINGS.participantValidationEmail
  }
  if (REGEX_EMAIL.test(val)) {
    return null
  }
  return STRINGS.participantValidationEmailInvalid
}

export const getPhoneError = ({ val, required, customValidation }) => {
  if (customValidation && !passCustomValidation({ val, customValidation })) {
    return STRINGS.participantValidationPhoneValid
  }
  if (!required) {
    return null
  }
  if (!val && required) {
    return STRINGS.participantValidationPhoneEmpty
  }
  if (!!val && isValidPhoneNumber(val)) return null
  return STRINGS.participantValidationPhoneValid
}

const getEmptyError = val => (val ? null : 'Should not be empty')

// export const getSiteError = ({ val, type = 'participant' }) => {
//   return val?.length < 1 ? `Please assign the ${type} to a site` : null
// }

export const getSiteError = ({ val, type = 'participant' }) => {
  return null
}

/**
 * Checks that dob object is not empty, and is not a future date
 * @param {Moment} dob
 */
function getDobError({ val, customValidation }) {
  if (customValidation && !passCustomValidation({ val, customValidation })) {
    return STRINGS.participantValidationDateOfBirthValid
  }
  if (!val || typeof val === 'undefined' || moment(val).format(DATE_FORMAT_MAP.datePicker) !== val) {
    return STRINGS.participantValidationDateOfBirthValid
  }
  if (moment().isBefore(val)) {
    return STRINGS.participantValidationDateOfBirthFuture
  }
  return null
}

function getBirthYearError({ val }) {
  const currentYear = new Date().getFullYear()
  const year = typeof val === 'string' ? parseInt(val, 10) : val

  if (!year || isNaN(year) || year.toString().length !== 4) {
    return 'Birth year must be a valid 4-digit year.'
  }

  if (year < 1900 || year > currentYear) {
    return `Birth year must be between 1900 and ${currentYear}.`
  }

  return null
}

export const errMsgVal = (key, val, val1, val2) =>
  val >= val1 && val <= val2 ? null : `${key} must be between ${val1} and ${val2}`

function getVsdError({ val, ptpData: { start_visit_id } }) {
  if (!val || typeof val === 'undefined') return STRINGS.participantValidationVisitDateValid
  // if (moment().isAfter(val, 'day')) return STRINGS.participantValidationVisitDatePast
  if (start_visit_id) {
    if (!start_visit_id || typeof start_visit_id === 'undefined') return STRINGS.participantValidationVisitNameValid
  }
  return null
}

function getVsIdError({ val, ptpData: { visit_start_datetime } }) {
  if (!visit_start_datetime || typeof visit_start_datetime === 'undefined')
    return STRINGS.participantValidationVisitDateValid
  if (!val || typeof val === 'undefined') return STRINGS.participantValidationVisitNameValid
  return null
}

function getSubjectIdError({ val, customValidation }) {
  if (customValidation && !passCustomValidation({ val, customValidation })) {
    return STRINGS.participantValidationValidSubjectId
  }
  if (!val) return STRINGS.participantValidationSubjectId
  return null
}

function getLocaleError({ val }) {
  if (!val || val.length === 0) {
    return STRINGS.participantValidationLocale
  }
  return null
}
function getTrackError({ val }) {
  if (!val || val.length === 0) {
    return STRINGS.participantValidationTrack
  }
  return null
}

export function validatePreScreenId({ val, customValidation, isMandatory = false }) {
  const trimmed = (val || '').trim()

  // Empty value: allowed for optional fields, required for mandatory ones
  if (!trimmed) {
    return isMandatory ? 'Pre-screen ID is required.' : null
  }

  // If there is a value, always enforce format when a regex is provided
  if (customValidation instanceof RegExp && !customValidation.test(trimmed)) {
    return 'Invalid Pre-screen ID format.'
  }

  return null
}

export function validateSubjectId({ val, customValidation, isMandatory = false }) {
  const trimmed = (val || '').trim()

  // Empty value: allowed for optional fields, required for mandatory ones
  if (!trimmed) {
    return isMandatory ? STRINGS.participantValidationSubjectId : null
  }

  // If there is a value, always enforce format when provided
  let regex
  if (typeof customValidation === 'string') {
    // custom_validations may be in /pattern/flags form when auto_gen_format is undefined
    regex = parseRegexFromCustomValidation(customValidation) || new RegExp(customValidation)
  } else if (customValidation instanceof RegExp) {
    regex = customValidation
  }

  if (regex && !regex.test(trimmed)) {
    return STRINGS.participantValidationValidSubjectId
  }

  return null
}

/**
 * Build a regex pattern from a format string dynamically.
 * Supports:
 * - {TAG:length} → editable segments with length constraints
 * - {TAG} → dynamic constants (non-editable, will be replaced by actual values)
 * - Text outside {} → static constants
 *
 * Fully dynamic: works with any tag name, not just hardcoded ones.
 */
function buildRegexFromFormatString(formatString) {
  if (!formatString || typeof formatString !== 'string') return null

  const tokenRegex = /\{([^}]+)\}/g
  let cursor = 0
  let regexStr = ''
  const escapeConst = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const pushConst = end => {
    if (end > cursor) {
      const piece = formatString.substring(cursor, end)
      regexStr += escapeConst(piece)
    }
  }

  /**
   * Build regex pattern for a token dynamically.
   * @param {string} token - The token content (e.g., "LETTERS:3" or "STUDY_ID")
   * @returns {string|null} - Regex pattern string or null to escape as literal
   */
  const buildTokenRegex = token => {
    const normalizedToken = token.toUpperCase().trim()
    const hasColon = normalizedToken.includes(':')

    // If no colon, it's a dynamic constant (non-editable)
    // We can't validate these without knowing their values, so we allow any content
    // In practice, these should be replaced with actual values before validation
    if (!hasColon) {
      // For constants like {STUDY_ID}, {SITE_ID}, allow alphanumeric with dashes
      // This is a safe fallback for unknown constants
      return '[A-Za-z0-9-]+'
    }

    // Has colon: editable segment {TAG:length} or {INCR:startValue}
    const [rule, valueStr] = normalizedToken.split(':')
    const normalizedRule = rule.trim().toUpperCase()

    // For INCR, valueStr is the starting value (e.g., "01", "001"), not length
    // The length is derived from the starting value's string length
    let length
    if (
      normalizedRule === ID_PATTERN_TOKENS.INCR ||
      normalizedRule === ID_PATTERN_TOKENS.INCREMENT ||
      normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL
    ) {
      // Length is determined by the format of the starting value
      length = valueStr ? valueStr.length : 1
    } else {
      // For other rules, valueStr is the length
      length = valueStr ? parseInt(valueStr, 10) : null
    }

    // Build regex pattern based on rule type
    const getRegexForRule = (ruleName, len) => {
      const normalizedRule = ruleName.toUpperCase()

      switch (normalizedRule) {
        case ID_PATTERN_TOKENS.LETTER:
        case ID_PATTERN_TOKENS.LETTERS:
        case ID_PATTERN_TOKENS.LETTERS_UPPERCASE:
          return REGEX_PARTS.upper(len)

        case ID_PATTERN_TOKENS.LETTERS_LOWERCASE:
        case ID_PATTERN_TOKENS.LETTER_LOWER:
          return REGEX_PARTS.lower(len)

        case ID_PATTERN_TOKENS.LETTERS_MIXED:
        case ID_PATTERN_TOKENS.LETTER_MIXED:
          return REGEX_PARTS.mixed(len)

        case ID_PATTERN_TOKENS.DIGITS:
        case ID_PATTERN_TOKENS.DIGIT:
          return REGEX_PARTS.digits(len)

        case ID_PATTERN_TOKENS.ALPHANUMERIC_UPPERCASE:
        case ID_PATTERN_TOKENS.ALNUM:
        case ID_PATTERN_TOKENS.ALNUM_UPPERCASE:
          return REGEX_PARTS.alnumUpperWithDigit(len)

        case ID_PATTERN_TOKENS.ALPHANUMERIC_LOWERCASE:
        case ID_PATTERN_TOKENS.ALNUM_LOWER:
        case ID_PATTERN_TOKENS.ALNUM_LOWERCASE:
          return REGEX_PARTS.alnumLowerWithDigit(len)

        case ID_PATTERN_TOKENS.ALPHANUMERIC_MIXED:
        case ID_PATTERN_TOKENS.ALNUM_MIXED:
          return REGEX_PARTS.alnumMixedWithDigit(len)

        case ID_PATTERN_TOKENS.INCR:
        case ID_PATTERN_TOKENS.INCREMENT:
        case ID_PATTERN_TOKENS.INCREMENTAL:
          return REGEX_PARTS.digits(len)

        case ID_PATTERN_TOKENS.HEX_UPPERCASE:
        case ID_PATTERN_TOKENS.HEX:
          return REGEX_PARTS.hexUpper(len)

        case ID_PATTERN_TOKENS.HEX_LOWERCASE:
          return REGEX_PARTS.hexLower(len)

        case ID_PATTERN_TOKENS.STUDY_ID:
        case ID_PATTERN_TOKENS.SITE_ID:
          return REGEX_PARTS.alnumUpper(len)

        default:
          if (normalizedRule.includes(ID_PATTERN_TOKENS.LETTER) || normalizedRule.includes(PATTERN_KEYWORDS.CHAR)) {
            return REGEX_PARTS.mixed(len)
          }
          if (normalizedRule.includes(ID_PATTERN_TOKENS.DIGIT) || normalizedRule.includes(PATTERN_KEYWORDS.NUM)) {
            return REGEX_PARTS.digits(len)
          }
          if (
            normalizedRule.includes(ID_PATTERN_TOKENS.ALNUM) ||
            normalizedRule.includes(PATTERN_KEYWORDS.ALPHANUMERIC)
          ) {
            return REGEX_PARTS.alnum(len)
          }
          return REGEX_PARTS.alnum(len)
      }
    }

    return getRegexForRule(rule, length)
  }

  let match
  while ((match = tokenRegex.exec(formatString)) !== null) {
    pushConst(match.index)
    const token = match[1]
    const regexPattern = buildTokenRegex(token)
    regexStr += regexPattern || escapeConst(match[0])
    cursor = match.index + match[0].length
  }
  pushConst(formatString.length)

  return regexStr ? new RegExp(`^${regexStr}$`) : null
}

/**
 * Validate INCR values are >= their starting values.
 * @param {string} id - The ID value to validate
 * @param {string} format - The format pattern
 * @param {object} constants - Optional map of constant values
 * @returns {boolean} - true if all INCR values are valid, false otherwise
 */
function validateIncrValues(id, format, constants = null) {
  if (!format || !id) return true

  const trimmedId = String(id).trim()
  const tokenRegex = /\{([^}]+)\}/g
  let match

  // Build format with constants replaced for parsing
  let formatForParsing = format
  if (constants && typeof constants === 'object') {
    formatForParsing = format.replace(/\{([^}:]+)\}/g, (match, constantName) => {
      if (!match.includes(':')) {
        const constantValue = constants[constantName]
        if (constantValue !== undefined && constantValue !== null) {
          return String(constantValue)
        }
      }
      return match
    })
  }

  // Parse format and extract INCR tokens with their positions
  const incrInfo = []
  let lastIndex = 0
  let editableSegmentIndex = 0

  tokenRegex.lastIndex = 0
  while ((match = tokenRegex.exec(format)) !== null) {
    const tokenContent = match[1]
    const hasColon = tokenContent.includes(':')

    if (hasColon) {
      const [rule, valueStr] = tokenContent.split(':')
      const normalizedRule = rule.trim().toUpperCase()

      if (
        normalizedRule === ID_PATTERN_TOKENS.INCR ||
        normalizedRule === ID_PATTERN_TOKENS.INCREMENT ||
        normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL
      ) {
        const startValue = valueStr || '01'
        incrInfo.push({
          startValue,
          length: startValue.length,
          editableSegmentIndex,
        })
      }
      editableSegmentIndex++
    }

    lastIndex = match.index + match[0].length
  }

  if (incrInfo.length === 0) return true // No INCR tokens to validate

  // Extract editable segment values from ID by parsing format structure
  const segmentValues = []
  let idCursor = 0
  lastIndex = 0
  editableSegmentIndex = 0

  tokenRegex.lastIndex = 0
  while ((match = tokenRegex.exec(formatForParsing)) !== null) {
    // Match literal text before token
    const before = formatForParsing.substring(lastIndex, match.index)
    if (before) {
      if (!trimmedId.substring(idCursor).startsWith(before)) {
        // Format mismatch - let regex validation handle it
        return true
      }
      idCursor += before.length
    }

    const tokenContent = match[1]
    const hasColon = tokenContent.includes(':')

    if (hasColon) {
      // Editable segment - extract value
      const [rule, valueStr] = tokenContent.split(':')
      const normalizedRule = rule.trim().toUpperCase()

      if (
        normalizedRule === ID_PATTERN_TOKENS.INCR ||
        normalizedRule === ID_PATTERN_TOKENS.INCREMENT ||
        normalizedRule === ID_PATTERN_TOKENS.INCREMENTAL
      ) {
        // INCR segment - extract based on starting value length
        const length = (valueStr || '01').length
        const value = trimmedId.substring(idCursor, idCursor + length)
        segmentValues[editableSegmentIndex] = value
        idCursor += length
      } else {
        // Other editable segment - extract based on length
        const length = valueStr ? parseInt(valueStr, 10) : null
        if (length) {
          const value = trimmedId.substring(idCursor, idCursor + length)
          segmentValues[editableSegmentIndex] = value
          idCursor += length
        } else {
          // Variable length - skip for now (would need more complex parsing)
          return true
        }
      }
      editableSegmentIndex++
    } else {
      // Constant - match and skip
      let constantValue = tokenContent
      if (constants && constants[tokenContent] !== undefined) {
        constantValue = String(constants[tokenContent])
      }
      if (trimmedId.substring(idCursor).startsWith(constantValue)) {
        idCursor += constantValue.length
      }
    }

    lastIndex = match.index + match[0].length
  }

  // Match remaining literal text
  const after = formatForParsing.substring(lastIndex)
  if (after && !trimmedId.substring(idCursor).startsWith(after)) {
    // Format mismatch - let regex validation handle it
    return true
  }

  // Validate each INCR segment
  for (const incr of incrInfo) {
    const segmentValue = segmentValues[incr.editableSegmentIndex]
    if (segmentValue) {
      const startValueNum = parseInt(incr.startValue, 10)
      const segmentValueNum = parseInt(segmentValue, 10)

      // Check if segment value is >= starting value
      if (isNaN(segmentValueNum) || segmentValueNum < startValueNum) {
        return false
      }
    }
  }

  return true
}

/**
 * Validate an ID against a format pattern.
 * @param {string} id - The ID value to validate
 * @param {string} format - The format pattern (e.g., "{LETTERS:3}-{SITE_ID}-{STUDY_ID}{ALNUM:1}-234")
 * @param {object} constants - Optional map of constant values (e.g., { STUDY_ID: "20363", SITE_ID: "31315" })
 * @returns {boolean} - true if valid, false if invalid
 */
export function validateIdByFormat(id, format, constants = null) {
  if (!format) return true // No format to validate against

  // Treat empty as "no value" here; required/optional is handled elsewhere.
  if (!id || String(id).trim() === '') return true

  // If constants are provided, replace constants in format with actual values for validation
  let formatForValidation = format
  if (constants && typeof constants === 'object') {
    // Replace {CONSTANT_NAME} with actual constant values in the format string
    formatForValidation = format.replace(/\{([^}:]+)\}/g, (match, constantName) => {
      // Only replace if no colon (i.e., it's a constant, not an editable segment)
      if (!match.includes(':')) {
        const constantValue = constants[constantName]
        if (constantValue !== undefined && constantValue !== null) {
          // Escape the constant value for regex
          return String(constantValue).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        }
      }
      return match
    })
  }

  const regex = buildRegexFromFormatString(formatForValidation)
  if (!regex) return true

  // Trim the ID before validation to remove any extra whitespace
  const trimmedId = String(id).trim()

  // First check if format matches
  if (!regex.test(trimmedId)) {
    return false
  }

  // If format matches, validate INCR values are >= starting values
  return validateIncrValues(id, format, constants)
}

const VALIDATION_FUNCTION_MAP = {
  fname: getNameError,
  lname: getNameError,
  email: getEmailError,
  phone: getPhoneError,
  date_of_birth: getDobError,
  site_id: getSiteError,
  visit_start_datetime: getVsdError,
  start_visit_id: getVsIdError,
  pre_screen_id: validatePreScreenId,
  subject_id: validateSubjectId,
  default_locale: getLocaleError,
  study_groups: getTrackError,
}

export const messureValidation = (messureName, unit, val) => {
  let errorText = null
  if (messureName === 'height') {
    switch (unit) {
      case 'cm':
        errorText = errMsgVal(unit, val, 50, 250)
        break
      case 'feet/inches': {
        if (!val) {
          errorText = 'Height is required.'
          break
        }

        const match = val.match(/^(\d)(?:\s(\d{1,2}))?$/)
        if (!match) {
          errorText = 'Invalid format. Use format: F II (e.g., 5 11)'
          break
        }

        const feet = parseInt(match[1], 10)
        const inches = match[2] ? parseInt(match[2], 10) : 0

        if (feet < 1 || feet > 8) {
          errorText = 'Feet must be between 1 and 8.'
        } else if (inches < 0 || inches > 11) {
          errorText = 'Inches must be between 0 and 11.'
        }
        break
      }
      default:
        break
    }
  }
  if (messureName === 'weight') {
    switch (unit) {
      case 'kg':
        errorText = errMsgVal(unit, val, 20, 300)
        break
      case 'pounds':
        errorText = errMsgVal(unit, val, 44, 661)
        break
      default:
        break
    }
  }
  return errorText
}

export function getParticipantErrors({ participantData, mandatoryValidations = [], customValidationsMap }) {
  const ptpFieldsWithValsArr = Object.keys(participantData).filter(field => participantData[field])
  const fieldsToValidate = uniqueArr([...mandatoryValidations, ...ptpFieldsWithValsArr])
  return fieldsToValidate
    .map(fieldToValidate => {
      const args = {}
      args.val = participantData[fieldToValidate]
      // Flag whether this field is mandatory so validators can distinguish
      // between required and optional fields.
      args.isMandatory = mandatoryValidations.includes(fieldToValidate)

      if (fieldToValidate === 'date_of_birth') {
        const isYearOnly = typeof args.val === 'number' || /^\d{4}$/.test(args.val)
        if (isYearOnly) {
          return getBirthYearError({ val: args.val })
        }
      }
      if (['weight', 'height'].includes(fieldToValidate)) {
        return getEmptyError(participantData[`unit_${fieldToValidate}`])
      }
      if (['unit_weight', 'unit_height'].includes(fieldToValidate)) {
        const [_, messureName] = fieldToValidate.split('_')
        return messureValidation(messureName, args.val, participantData[messureName])
      }
      if (fieldToValidate === 'visit_start_datetime' || fieldToValidate === 'start_visit_id') {
        args.ptpData = participantData
      }

      if (customValidationsMap[fieldToValidate]) args.customValidation = customValidationsMap[fieldToValidate]
      return VALIDATION_FUNCTION_MAP[fieldToValidate].apply(this, [args])
    })
    .filter(e => e !== null)
}

export const isPtpInUncompletedConsentBlockList = (ptpList, consentBlockList) =>
  Object.values(consentBlockList)
    .flat()
    .some(ptp => ptpList.includes(ptp))
